import {
  Box,
  Flex,
  Heading,
  Link,
  SkeletonText,
  Spinner,
  SvgIcon
} from '@volue/wave-react';
import { isNil } from 'ramda';
import React, { useEffect } from 'react';

import useFeatureToggles from '@/common/hooks/feature-toggles';
import { match } from '@/common/utils/match';
import type {
  CalculationState,
  SelectedGridElement
} from '@/features/execute-calculation-set/overmind/calculation';
import type { EvaluatedGridElementType } from '@/features/execute-calculation-set/overmind/calculation/derived-state/gridElementsDetailsByCalculationType/GridElementType';
import { useActions, useAppState } from '@/overmind';

import { ACLineDetails } from './ACLineDetails';
import { BusbarDetails } from './BusbarDetails';
import { EnergyConsumerDetails } from './EnergyConsumerDetails';
import { ExternalNetworkDetails } from './ExternalNetworkDetails';
import { PowerTransformerDetails } from './PowerTransformerDetails';
import { GridElementCalculationSetExecutionDetails, GridElementIdentifierHeader } from './three-loading-lv-scenario/GridElementCalculationSetExecutionDetails';
import { useFeatureDetails } from './useFeatureDetails';

function getGridElementIcon(
  objectType: EvaluatedGridElementType
): JSX.Element | null {
  const icon = match.someOf(objectType, {
    EnergyConsumer: () => <SvgIcon iconName="home" />,
    AcLineSegment: () => <SvgIcon iconName="minus" />,
    PowerTransformer: () => <SvgIcon iconName="building" />,
    default: () => null
  });

  return icon;
}

export const GridElementCalculationTacticResults = () => {
  const selectedGridElement = useAppState().calculation.selectedGridElement;
  if (
    selectedGridElement.type === 'SELECTED_GRID_ELEMENT' &&
    selectedGridElement.gridElement.loadflow.type ===
      'ALL_LOAD_FLOW_SCENARIO_DETAILS'
  ) {
    return <GridElementCalculationSetExecutionDetails />;
  }

  return <_GridElementCalculationTacticResults />;
};

const _GridElementCalculationTacticResults = () => {
  const initiatingGridElement = useAppState().calculation.initiatingGridElement;
  const selectedGridElement = useAppState().calculation.selectedGridElement;
  const browseResultsSummary = useActions().calculation.browseResultsSummary;
  const featureFlags = useFeatureToggles();

  if (selectedGridElement.type === 'NO_SELECTED') {
    return (
      <span
        data-component={GridElementCalculationSetExecutionDetails.name}
        style={{ display: 'contents' }}
      >
        <Flex className="pAm" flow={'column'}>
          <Box>
            <Flex as={Link} onClick={browseResultsSummary}>
              <Box>
                <SvgIcon iconName="arrowLeft" className="mRs" />
                {'back to results'}
              </Box>
            </Flex>
          </Box>
          <Box>{'No information available for this grid element'}</Box>
        </Flex>
      </span>
    );
  }

  return (
    <span
      data-component={GridElementCalculationSetExecutionDetails.name}
      style={{ display: 'contents' }}
    >
      <Flex className="pAm" flow={'column'}>
        <Box>
          <Flex as={Link} onClick={browseResultsSummary}>
            <Box>
              <SvgIcon iconName="arrowLeft" className="mRs" />
              {'back to results'}
            </Box>
          </Flex>
        </Box>
        <GridElementIdentifierHeader
          featureFlags={featureFlags}
          initiatingGridElement={initiatingGridElement}
          selectedGridElement={selectedGridElement}
        />

        {
          /*
        NOTE: this is how we can achieve the type safety but unfortunately it is not the perfect way...
        */

          match.someOf(selectedGridElement.gridElement.objectType, {
            EnergyConsumer: () => (
              <EnergyConsumerDetails
                energyConsumer={
                  selectedGridElement as {
                    type: 'SELECTED_GRID_ELEMENT';
                    gridElement: SelectedGridElement<'EnergyConsumer'>;
                  }
                }
              />
            ),
            Busbar: () => (
              <BusbarDetails
                busbar={
                  selectedGridElement as {
                    type: 'SELECTED_GRID_ELEMENT';
                    gridElement: SelectedGridElement<'Busbar'>;
                  }
                }
              />
            ),
            ExternalNetworkInjection: () => (
              <ExternalNetworkDetails
                externalNetworkInjection={
                  selectedGridElement.gridElement as SelectedGridElement<'ExternalNetworkInjection'>
                }
              />
            ),
            PowerTransformer: () => (
              <PowerTransformerDetails
                powerTransformer={
                  selectedGridElement as {
                    type: 'SELECTED_GRID_ELEMENT';
                    gridElement: SelectedGridElement<'PowerTransformer'>;
                  }
                }
              />
            ),
            AcLineSegment: () => (
              <ACLineDetails
                acline={
                  selectedGridElement as {
                    type: 'SELECTED_GRID_ELEMENT';
                    gridElement: SelectedGridElement<'AcLineSegment'>;
                  }
                }
              />
            ),
            default: () => null
          })
        }
        <GridElementDetails
          gridElementDetails={selectedGridElement}
          useTitle={() => {}}
        />
      </Flex>
    </span>
  );
};

type BrowsedGridElement = Extract<
  CalculationState['selectedGridElement'],
  {
    type: 'SELECTED_GRID_ELEMENT';
  }
>;

export const GridElementDetails: React.FunctionComponent<{
  gridElementDetails: BrowsedGridElement;
  useTitle: (title: string | undefined) => void;
}> = ({ gridElementDetails, useTitle }) => {
  const featureFlags = useFeatureToggles();
  const featureDetailsRef = React.useRef(null);
  const featureDetails = useFeatureDetails({
    of: gridElementDetails.gridElement.objectId,
    displayIn: featureDetailsRef
  });
  if (isNil(featureDetails)) return null;
  if (featureFlags.isShowingGridElementTitleEnabled()) {
    useTitleWhenAvailable(featureDetails, useTitle);
  }

  return (
    <Flex flow="column" justifyContent={'center'} alignItems="center">
      {featureDetails.status === 'LOADING' && (
        <Flex alignItems="center" css={{ marginTop: '5rem' }}>
          Loading grid element details... <Spinner inline size="small" />
        </Flex>
      )}
      {featureDetails.status === 'LOADED' && (
        <div ref={featureDetailsRef} style={{ width: '100%' }} />
      )}
      {featureDetails.status === 'CANNOT_BE_LOADED' && (
        <Flex alignItems="center" css={{ marginTop: '5rem' }}>
          Grid element details cannot be loaded.
        </Flex>
      )}
    </Flex>
  );
};
function useTitleWhenAvailable(
  featureDetails: NonNullable<ReturnType<typeof useFeatureDetails>>,
  useTitle: (title: string | undefined) => void
) {
  useEffect(() => {
    if (featureDetails.status === 'LOADED' && !featureDetails.title) return;
    useTitle(featureDetails.title);
  }, [featureDetails.title]);
}

function anyAttentionRequiringEvaluatedResults(selectedGridElement: {
  type: 'SELECTED_GRID_ELEMENT';
  gridElement: SelectedGridElement<any>;
}) {
  return selectedGridElement.gridElement.attentionRequiringResultsCount > 0;
}
